import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Segment, Statistic, Button, Icon, List } from 'semantic-ui-react';

import { CenteredContent } from 'src/style';
import { featureToggleUtil } from '../../utils';
import CreditBalanceChip from 'src/components/credits/CreditBalanceChip';
import OutOfCreditsModal from 'src/components/credits/OutOfCreditsModal';
import { useCredits, CreditHistoryItem } from 'src/hooks/useCredits';
import { useFeatureFlags } from 'src/hooks/useFeatureFlags';

const MyCenteredContent = styled(CenteredContent)`
  padding-left: 1rem;
  padding-right: 1rem;
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const Title = styled.h1`
  flex: 1;
  font-size: 1.5em;
  font-weight: bold;
  margin: 0;
`;

export const iconForType = (type: string) => {
  switch (type) {
    case 'grant':
    case 'grant_monthly':
    case 'monthly_grant':
      return 'gift';
    case 'purchase':
    case 'pack':
      return 'shopping cart';
    case 'consume':
    case 'usage':
      return 'lightning';
    case 'refund':
      return 'undo';
    default:
      return 'circle';
  }
};

type HistoryItemForLabel = Pick<CreditHistoryItem, 'type' | 'feature'>;
type Translate = (key: string, fallback: string) => string;

const historyFeatureLabel = (feature: string | undefined, t: Translate) => {
  switch (feature) {
    case 'audiobook':
      return t('CreditHistoryAudiobook', 'Audiobook');
    case 'audiobook_voice_sample':
      return t('CreditHistoryAudiobookVoiceSample', 'Audiobook voice sample');
    case 'feedback_digest':
      return t('CreditHistoryFeedbackDigest', 'Feedback digest');
    case 'editor_feedback':
      return t('CreditHistoryEditorFeedback', 'Editorial feedback');
    case 'editor_feedback_rerun':
      return t('CreditHistoryEditorFeedbackRerun', 'Editorial feedback rerun');
    case 'classifier':
      return t('CreditHistoryClassifier', 'Manuscript classification');
    case 'summarize':
      return t('CreditHistorySummarize', 'Manuscript summary');
    case 'publishability':
      return t('CreditHistoryPublishability', 'Publishability analysis');
    case 'tts':
      return t('CreditHistoryTts', 'Text to speech');
    case 'submit_inbox':
      return t('CreditHistorySubmitInbox', 'Manuscript submission');
    default:
      return undefined;
  }
};

export const historyItemLabel = (item: HistoryItemForLabel, t: Translate) => {
  switch (item.type) {
    case 'grant_monthly':
    case 'monthly_grant':
      return t('MonthlyCreditGrant', 'Monthly credit grant');
    case 'grant':
    case 'grant_manual':
      return t('CreditHistoryGrant', 'Credit grant');
    case 'purchase':
    case 'pack':
      return t('CreditHistoryPurchase', 'Credit purchase');
    case 'revoke_purchase':
      return t('CreditHistoryRevokePurchase', 'Credit purchase revoked');
    case 'restore_purchase':
      return t('CreditHistoryRestorePurchase', 'Credit purchase restored');
    case 'expire':
      return t('CreditHistoryExpire', 'Credits expired');
    case 'refund':
      return [
        t('CreditHistoryRefund', 'Credit refund'),
        historyFeatureLabel(item.feature, t),
      ]
        .filter(Boolean)
        .join(' · ');
    case 'consume':
    case 'usage':
      return (
        historyFeatureLabel(item.feature, t) ||
        t('CreditHistoryUsage', 'Credit usage')
      );
    default:
      return t('CreditHistoryTransaction', 'Credit transaction');
  }
};

// `embedded` renders this as a subsection (e.g. inside /account/subscription):
// it drops the page <Helmet> title and, when the AI editor is unavailable,
// renders nothing instead of a standalone "not available" panel.
const BookCredits: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { t } = useTranslation();
  const currentBook = useSelector((state: any) => state.book.currentBook);
  const userProfile = useSelector((state: any) => state.user.userProfile);
  const { data: credits } = useCredits();
  const { flags, isLoading: flagsLoading } = useFeatureFlags();
  const aiEditorEnabled =
    flags?.ai_editor === true ||
    featureToggleUtil.hasFeatureAccess(userProfile, 'ai_editor');
  const [modalOpen, setModalOpen] = useState(false);

  const balance = credits?.balance ?? 0;
  const period = credits?.period;
  const history = credits?.history || [];

  if (!flagsLoading && !aiEditorEnabled) {
    if (embedded) return null;
    return (
      <MyCenteredContent>
        <Segment>
          {t('FeatureNotAvailable', 'This feature is not available.')}
        </Segment>
      </MyCenteredContent>
    );
  }

  return (
    <>
      {!embedded && (
        <Helmet>
          <meta charSet="utf-8" />
          <title>{t('UsageAndCredits', 'Usage & credits')}</title>
        </Helmet>
      )}
      <MyCenteredContent>
        <HeaderRow>
          <Title>{t('UsageAndCredits', 'Usage & credits')}</Title>
          {currentBook && (
            <CreditBalanceChip usageHref="/account/subscription" />
          )}
        </HeaderRow>

        {/* Current balance */}
        <Segment>
          <Statistic size="small">
            <Statistic.Value>
              <Icon name="lightning" /> {balance}
            </Statistic.Value>
            <Statistic.Label>{t('CreditsBalance', 'Credits')}</Statistic.Label>
          </Statistic>
          {credits?.resetDate && (
            <p style={{ color: '#666' }}>
              {credits?.productInternalId === 'free'
                ? t('FreePlan', 'Free plan')
                : t('PremiumPlan', 'Premium plan')}{' '}
              ·{' '}
              {t('ResetsOn', 'resets {{date}}', {
                date: new Date(credits.resetDate).toLocaleDateString(),
              })}{' '}
              · {credits?.monthlyCredits ?? 0}/{t('Month', 'mo')}
            </p>
          )}
          <Button
            color="orange"
            onClick={() => setModalOpen(true)}
            content={t('BuyMoreCredits', 'Buy more credits')}
          />
        </Segment>

        {/* Period stats */}
        {period && (
          <Segment>
            <Statistic.Group widths="one" size="tiny">
              <Statistic>
                <Statistic.Value>{balance}</Statistic.Value>
                <Statistic.Label>{t('Remaining', 'Remaining')}</Statistic.Label>
              </Statistic>
            </Statistic.Group>
          </Segment>
        )}

        {/* History */}
        <Segment>
          <h4>{t('History', 'History')}</h4>
          <List divided relaxed>
            {history.map((item: CreditHistoryItem) => (
              <List.Item key={item.id}>
                <List.Icon
                  name={iconForType(item.type)}
                  size="large"
                  verticalAlign="middle"
                />
                <List.Content>
                  <List.Header>{historyItemLabel(item, t)}</List.Header>
                  <List.Description>
                    {new Date(item.date).toLocaleDateString()}
                    {item.meta?.reason ? ` · ${item.meta.reason}` : ''}
                  </List.Description>
                </List.Content>
                <List.Content floated="right">
                  <span
                    style={{ color: item.amount >= 0 ? '#28a745' : '#c00' }}
                  >
                    {item.amount >= 0 ? '+' : '−'}
                    {Math.abs(item.amount)}
                  </span>
                </List.Content>
              </List.Item>
            ))}
          </List>
        </Segment>
      </MyCenteredContent>
      <OutOfCreditsModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
};

export default BookCredits;
